import { areaCountryOpts as areaCountryOptsFun, ErrorCode, industryLabelSort, IndustryTypes, TRIGGER_TYPES, UserStatus } from '@/constants';
import { industryLabels as industryLocalLabels } from '@/constants/intlObjs';
import useLanguage from '@/hooks/useLanguage';
import { Locales } from '@/hooks/useLocal';
import { HeaderTypography, Policy } from '@/layout/LoginLayout';
import { changeStatus } from '@/redux/slice/user';
import { fillAdvertiserInfo, FillAdvertiserInfoParams } from '@/services/login';
import { getParentContainer } from '@/utils/util';
import { Button, Cascader, Form, Input, message, Select } from 'antd';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { YOUDAOADS_REGISTER_INFO } from '../register/contents';
import styles from './index.module.scss';
import PhoneNumberSelect, { getAreaByCountryId, getCountryByAreaNameAndCountryId, PhoneNumberSelectValue } from './PhoneNumberSelect';
import MobileCountryPicker from '@/components/form/MobileCountryPicker';
import useIsMobile from '@/hooks/useIsMobile';

export const scales = ['未知', '<15', '15-50', '50-100', '100-500', '500-2000', '>2000'];

export const intlMessages = defineMessages({
  title: {
    defaultMessage: 'Company Information',
  },
  companyName: {
    defaultMessage: 'Company Name',
  },
  industry: {
    defaultMessage: 'Industry',
  },
  country: {
    description: '国家/地区',
    defaultMessage: 'Country/Region',
  },
  scale: { defaultMessage: 'Scale of Company' },
  contactName: {
    description: '联系人姓名',
    defaultMessage: 'Contact Name',
  },
  phoneNumber: {
    defaultMessage: 'Phone Number',
  },
  submit: {
    defaultMessage: 'Submit',
  },
});

export const placeholders = defineMessages({
  companyNamePlaceholder: {
    defaultMessage: 'Please enter a valid company name',
  },
  industryPlaceholder: {
    defaultMessage: 'Please select an industry',
  },
  otherIndustryPlaceholder: {
    description: '请输入所属行业',
    defaultMessage: 'Please enter an industry',
  },
  countryPlaceholder: {
    defaultMessage: 'Please select your country or region',
  },
  scalePlaceholder: {
    defaultMessage: 'Please select the scale of your company',
  },
  contactNamePlaceholder: {
    defaultMessage: 'Please enter a contact name',
  },
  phoneNumberCountryPlaceholder: {
    defaultMessage: 'Please select your country or region',
  },
  phoneNumberDetailPlaceholder: {
    defaultMessage: 'Please enter',
  },
});

export const errors = defineMessages({
  wrong: {
    defaultMessage: 'Something is wrong',
  },
  companyNameRequiredError: {
    defaultMessage: 'Please enter a valid company name',
  },
  industryRequiredError: {
    defaultMessage: 'Please select an industry',
  },
  otherIndustryDetail: {
    description: '请输入行业',
    defaultMessage: 'Please enter an industry',
  },
  countryRequiredError: {
    defaultMessage: 'Please select your country or region',
  },
  scaleRequiredError: {
    defaultMessage: 'Please select the scale of your company',
  },
  contactNameRequiredError: {
    defaultMessage: 'Please enter a contact name',
  },
  repeatedSubmitError: {
    defaultMessage: 'Repeated submission of info',
  },
  phoneNumberCountryRequiredError: {
    defaultMessage: 'Please select country',
  },
  phoneNumberNotValidError: {
    defaultMessage: 'Formal error. Please enter the correct phone number',
  },
  phoneNumberRequiredError: {
    defaultMessage: 'Please enter phone number',
  },
});

export const rules = {
  companyName: [
    {
      required: true,
      message: <FormattedMessage defaultMessage="Please enter a valid company name" />,
    },
  ],
  industryType: [
    {
      required: true,
      message: <FormattedMessage defaultMessage="Please select an industry" />,
    },
  ],
  industryTypeName: [
    {
      required: true,
      message: <FormattedMessage description="请输入行业" defaultMessage="Please enter an industry" />,
    },
  ],
  country: [
    {
      required: true,
      message: <FormattedMessage defaultMessage="Please select your country or region" />,
    },
  ],
  scale: [
    {
      required: true,
      message: <FormattedMessage defaultMessage="Please select the scale of your company" />,
    },
  ],
  contactName: [
    {
      required: true,
      message: <FormattedMessage defaultMessage="Please enter a contact name" />,
    },
  ],
};

type FormData = Pick<FillAdvertiserInfoParams, 'companyName' | 'contactName' | 'scale' | 'industryType'> & {
  industryTypeName: string;
  country: [string, number];
  phoneNumber: PhoneNumberSelectValue;
  industryType: IndustryTypes;
};

const AdvertiserApply: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [form] = Form.useForm<FormData>();
  const [industryType, setIndustryType] = useState<IndustryTypes>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const router = useRouter();
  const dispatch = useDispatch();
  const { getI18nValue } = useLanguage();
  const isMobile = useIsMobile();

  useEffect(() => {
    try {
      //填充注册时填的值
      const initFormValue = localStorage.getItem(YOUDAOADS_REGISTER_INFO);
      if (!initFormValue) return;
      const value = JSON.parse(initFormValue);
      form.setFieldsValue({
        companyName: value?.companyName,
        scale: value?.scale,
      });
      localStorage.removeItem(YOUDAOADS_REGISTER_INFO);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const { industryLabels, areaCountryOpts } = useMemo(() => {
    const industryObj: Partial<Record<IndustryTypes, string>> = {};
    (industryLabelSort as unknown as IndustryTypes[]).forEach((key) => {
      industryObj[key] = industryLocalLabels[locale as Locales][key];
    });
    return {
      industryLabels: industryObj,
      areaCountryOpts: areaCountryOptsFun(locale),
    };
  }, [locale]);

  useEffect(() => {
    const formCountryValue = form.getFieldValue('country');
    if (formCountryValue && Array.isArray(formCountryValue) && formCountryValue.length === 2) {
      const [_, countryId] = formCountryValue;
      const area = getAreaByCountryId(countryId)!;
      const areaKey = getI18nValue('areaCountryOneMap');
      form.setFieldsValue({
        country: [area[areaKey], countryId],
      });
    }
  }, [locale]);

  const handleFinish = async (values: FormData) => {
    const { industryType, industryTypeName, country, phoneNumber: phoneNumberValue, ...rest } = values;
    const countryNameKey = getI18nValue('areaCountryTwoMap');
    const { phoneCode = '', phoneNumber, countryId } = phoneNumberValue || {};
    try {
      setSubmitLoading(true);
      await fillAdvertiserInfo({
        ...rest,
        countryId: country[1],
        countryName: getCountryByAreaNameAndCountryId(country, getI18nValue('areaCountryOneMap'))?.[countryNameKey] || '',
        industryType,
        industry: industryType === IndustryTypes.OTHER ? industryTypeName : industryLabels[industryType]!,
        phoneNumber: countryId && phoneNumber ? `${phoneCode} ${phoneNumber}`.trim() : undefined,
        phoneCountryId: countryId,
      });
      Cookies.set('USER_STATUS', Number(UserStatus.NORMAL).toString());
      dispatch(changeStatus(UserStatus.NORMAL));
      router.push('/advertising/promotion?firstLogin=true');
    } catch (e: any) {
      switch (e.code) {
        case ErrorCode.ADVERTISER_FILL_INFO_REPEAT:
          message.error({
            content: formatMessage(errors.repeatedSubmitError),
            className: 'message-error',
          });
          break;
        default:
          message.error({
            content: formatMessage(errors.wrong),
            className: 'message-error',
          });
          break;
      }
    }
    setSubmitLoading(false);
  };
  const handleBack = () => {
    router.push('/login');
  };

  const updateScreenSize = () => {
    setScreenWidth(window.innerWidth);
  };
  useEffect(() => {
    // 添加事件监听器
    window.addEventListener('resize', updateScreenSize);
    // 组件卸载时移除事件监听器
    return () => {
      window.removeEventListener('resize', updateScreenSize);
    };
  }, []);

  return (
    <div className={styles.container}>
      {/* <BackButton callback={handleBack} /> */}
      <HeaderTypography title={formatMessage(intlMessages.title)} />
      <Form form={form} onFinish={handleFinish} layout="vertical" name="advertiser_apply" requiredMark={true} className={styles.form}>
        <Form.Item
          name="companyName"
          label={formatMessage(intlMessages.companyName)}
          rules={rules.companyName}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
        >
          <Input allowClear placeholder={formatMessage(placeholders.companyNamePlaceholder)} maxLength={100} />
        </Form.Item>

        <Form.Item
          name="industryType"
          label={formatMessage(intlMessages.industry)}
          rules={rules.industryType}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
          style={{
            ...(industryType === IndustryTypes.OTHER && { marginBottom: 8 }),
          }}
        >
          <Select
            placeholder={formatMessage(placeholders.industryPlaceholder)}
            options={industryLabelSort.map((key) => ({
              value: key,
              label: industryLabels[key],
            }))}
            getPopupContainer={getParentContainer}
            allowClear
            onChange={(e) => setIndustryType(e)}
          />
        </Form.Item>

        {industryType === IndustryTypes.OTHER && (
          <Form.Item name="industryTypeName" rules={rules.industryTypeName} validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}>
            <Input placeholder={formatMessage(placeholders.otherIndustryPlaceholder)} maxLength={100} />
          </Form.Item>
        )}

        <Form.Item
          name="country"
          label={formatMessage(intlMessages.country)}
          rules={rules.country}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
        >
          {isMobile ? (
            <MobileCountryPicker placeholder={formatMessage(placeholders.countryPlaceholder)} />
          ) : (
            <Cascader
              displayRender={(label) => label[label.length - 1]}
              options={areaCountryOpts}
              placeholder={formatMessage(placeholders.countryPlaceholder)}
              getPopupContainer={getParentContainer}
              popupClassName={styles.cascaderPopup}
            />
          )}
        </Form.Item>

        <Form.Item
          name="scale"
          label={formatMessage(intlMessages.scale)}
          rules={rules.scale}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
        >
          <Select
            placeholder={formatMessage(placeholders.scalePlaceholder)}
            options={scales
              .slice(1) /** 去掉`未知` */
              .map((s, i) => ({
                label: s,
                value: i + 1 /** 去掉未知 所有索引 +1 */,
              }))}
            getPopupContainer={getParentContainer}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="contactName"
          label={formatMessage(intlMessages.contactName)}
          rules={rules.contactName}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
        >
          <Input allowClear placeholder={formatMessage(placeholders.contactNamePlaceholder)} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label={formatMessage(intlMessages.phoneNumber)}
          rules={[
            {
              validator: async (_, value: Partial<PhoneNumberSelectValue>) => {
                if (!value) return;

                if (!value.phoneCode && !value.phoneNumber) return;

                if (!value.phoneCode && value.phoneNumber) {
                  throw Error(formatMessage(errors.phoneNumberCountryRequiredError));
                }

                if (!value.phoneNumber && value.phoneCode) {
                  throw Error(formatMessage(errors.phoneNumberRequiredError));
                }

                if (!/^\d{1,15}$/.test(value.phoneNumber!)) {
                  throw Error(formatMessage(errors.phoneNumberNotValidError));
                }
              },
              validateTrigger: [TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit],
            },
          ]}
          validateTrigger={[TRIGGER_TYPES.onBlur, TRIGGER_TYPES.onSubmit]}
        >
          <PhoneNumberSelect
            screenWidth={screenWidth}
            isMobile={isMobile}
            areaCountryOpts={areaCountryOpts}
            onBlur={() => form.validateFields(['phoneNumber'])}
            onValidate={() => form.validateFields(['phoneNumber'])}
          />
        </Form.Item>

        <Button loading={submitLoading} type="primary" htmlType="submit" block className={styles.submitBtn}>
          {formatMessage(intlMessages.submit)}
        </Button>
      </Form>

      <Policy />
    </div>
  );
};

export default AdvertiserApply;
